import { parse, serialize } from 'cookie';

export type SetCookieOptions = {
  maxAgeSeconds: number;
  sameSite?: 'lax' | 'strict' | 'none';
  /**
   * Cookie domain. Omit for a host-only cookie (the default). Pass a
   * leading-dot domain (e.g. `.respondent.io`) for a cookie that has to be
   * readable by every surface on the apex domain. Browsers reject a domain
   * the current host is not a subdomain of, so callers must derive it from
   * `location.hostname` rather than hardcoding it.
   */
  domain?: string;
};

function isBrowser(): boolean {
  return typeof document !== 'undefined';
}

/** Returns the trimmed cookie value, or undefined when missing/SSR/empty. */
export function getCookie(name: string): string | undefined {
  if (!isBrowser()) return undefined;
  const cookies = parse(document.cookie);
  const value = cookies[name]?.trim();
  return value || undefined;
}

/** Writes a Lax, path=/ cookie. Auto-flags `Secure` on https origins. */
export function setBrowserCookie(
  name: string,
  value: string,
  options: SetCookieOptions,
): void {
  if (!isBrowser()) return;
  const expires = new Date(Date.now() + options.maxAgeSeconds * 1000);
  const isHttps = globalThis.location?.protocol === 'https:';
  // eslint-disable-next-line unicorn/no-document-cookie -- single unified writer
  document.cookie = serialize(name, value, {
    path: '/',
    expires,
    sameSite: options.sameSite ?? 'lax',
    secure: isHttps || undefined,
    domain: options.domain,
  });
}

export function deleteCookie(
  name: string,
  options?: { domain?: string },
): void {
  if (!isBrowser()) return;
  // eslint-disable-next-line unicorn/no-document-cookie -- single unified writer
  document.cookie = serialize(name, '', {
    path: '/',
    expires: new Date(0),
    sameSite: 'lax',
    domain: options?.domain,
  });
}
